import type { DataTableCell, DataTableViewData } from '@shared/types/views';
import { formatBusinessTableValue } from '@/components/execution/renderers/DataTableRenderer';

/**
 * Reading of one result row in its fiche (08/10/2026). Pure arrangement of the
 * row's own cells by their shape: nothing is invented, nothing is hidden.
 */

type Row = DataTableViewData['rows'][number];

/** A column naming a technical key of the element rather than the element itself. */
const IDENTIFIER_LABEL = /^(identifiant|id|réf(érence)?|ref|repère|slug|clé)\b/i;
const IDENTIFIER_KEY = /^(id|identifiant|ref|reference|slug|key)(_|$)/i;
/** Layout threshold: up to this many characters a value reads as a short fact, beyond as a paragraph. */
export const FACT_MAX_LENGTH = 80;

export interface DetailField {
  key: string;
  label: string;
  cell: DataTableCell;
  text: string;
}

export interface RowDetailModel {
  title: string;
  /** The technical key shown under the title when the name comes from another column. */
  identifier: { label: string; value: string } | null;
  /** Decision-like values (tone given by the producer, or the table's quick filter column). */
  statuses: DetailField[];
  facts: DetailField[];
  paragraphs: DetailField[];
  /** Multi-line texts (an email draft, a note): kept as written, copyable. */
  documents: DetailField[];
  /** Labels of the fields without a value. */
  empty: string[];
}

export const detailText = (value: unknown, columnKey = ''): string => {
  value = formatBusinessTableValue(value, columnKey);
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return value.toLocaleString('fr-FR');
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const isEmptyText = (text: string) => text === '' || text === '—';

export const buildRowDetail = (row: Row, labels: Record<string, string>, statusKey: string | null): RowDetailModel => {
  const label = (key: string) => labels[key] ?? key;
  const fields = row.cells.map((cell) => ({ key: cell.key, label: label(cell.key), cell, text: detailText(cell.value, cell.key) }));
  const isStatus = (field: DetailField) => Boolean(field.cell.tone) || field.key === statusKey;
  const [first, ...rest] = fields;
  // The table's first column is the element; when it holds a technical key
  // (« Identifiant »), the fiche is titled by the next short named value.
  const firstIsKey = first !== undefined && (IDENTIFIER_LABEL.test(first.label) || IDENTIFIER_KEY.test(first.key));
  const name = firstIsKey
    ? rest.find((field) => typeof field.cell.value === 'string' && !isEmptyText(field.text) && !field.text.includes('\n')
      && field.text.length <= FACT_MAX_LENGTH && !isStatus(field))
    : undefined;
  const titleField = name ?? first;
  const others = fields.filter((field) => field !== titleField && !(name && field === first));

  const model: RowDetailModel = {
    title: titleField && !isEmptyText(titleField.text) ? titleField.text : '—',
    identifier: name && first && !isEmptyText(first.text) ? { label: first.label, value: first.text } : null,
    statuses: [], facts: [], paragraphs: [], documents: [], empty: [],
  };
  for (const field of others) {
    if (isEmptyText(field.text)) model.empty.push(field.label);
    else if (isStatus(field)) model.statuses.push(field);
    else if (field.text.includes('\n')) model.documents.push(field);
    else if (field.text.length <= FACT_MAX_LENGTH || typeof field.cell.value !== 'string') model.facts.push(field);
    else model.paragraphs.push(field);
  }
  return model;
};

/** « Objet : … » on the first line of a draft: the subject apart from the body. */
export const splitSubject = (text: string): { subject: string | null; body: string } => {
  const match = text.match(/^\s*(?:objet|subject)\s*:\s*([^\n]+)\n+([\s\S]*)$/i);
  return match ? { subject: match[1]!.trim(), body: match[2]!.trim() } : { subject: null, body: text.trim() };
};

export type TextPart = { kind: 'text'; text: string } | { kind: 'link'; href: string; label: string };

const URL_PATTERN = /https?:\/\/[^\s<>"«»]+/gi;
/** Punctuation that ends a sentence after a link rather than the link itself. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

const linkLabel = (href: string): string => {
  try {
    return new URL(href).hostname.replace(/^www\./i, '');
  } catch {
    return href.replace(/^https?:\/\//i, '');
  }
};

/** A text with its links apart, so a long URL reads as its site. */
export const textParts = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  let cursor = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const href = match[0].replace(TRAILING_PUNCTUATION, '');
    const start = match.index ?? 0;
    if (start > cursor) parts.push({ kind: 'text', text: text.slice(cursor, start) });
    parts.push({ kind: 'link', href, label: linkLabel(href) });
    cursor = start + href.length;
  }
  if (cursor < text.length) parts.push({ kind: 'text', text: text.slice(cursor) });
  return parts;
};
